import {
  CSSProperties,
  ElementRef,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  CardLink,
  Heading,
  PostImagePreview,
  SharePopup,
  SocialLinkPreview,
  TogetherTypes,
} from '@hhgtech/hhg-components'
import { DISCOVER_PATHS, TOGETHER_PATHS } from 'api/paths'
import {
  callApiWithAuth,
  getTogetherPath,
  redirectToSSOWithReturnUrl,
} from 'api/utils'
import { domainLocales } from 'domainLocales'
import innertext from 'innertext'
import {
  DEFAULT_AVATAR_IMG,
  DEFAULT_IMG,
  ENV,
  TRACKING_CATEGORY,
} from 'interfaces/constants'
import { UserInfo } from 'interfaces/types'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'
import { AppContext } from 'state/appContext'

import { PATHS } from '../api/paths'
import { CardReply } from '../cardReply'
import { TogetherDataContext } from '../common/TogetherDataWrapper'
import { useAuthRequiredAction } from '../common/TogetherDataWrapper/hooks/useAuthRequiredAction'
import { useLikeUnlikePost } from '../common/TogetherDataWrapper/hooks/usePostAction'
import { PostActions } from '../common/TogetherDataWrapper/state/postReducer'
import { InputCommentBar } from '../inputCommentBar'
import { CardReactionType } from '../interfaces/types'
import { Comment, Community, Post, Topic } from '../interfaces/types'
import { _communityHref } from '../utils'
import { formatComment } from '../utils/dataTransform'
import { CardPostPopoverMore } from './cardPostPopover'
import {
  StyledCardAuthorWrapper,
  StyledCardPost,
  StyledCardReactions,
  StyledCardReplyWrapper,
  StyledInnerPadding,
  StyledInputCommentBar,
  StyledLinkOverlay,
  StyledPin,
  StyledPostContent,
  StyledPostContentLinkWrapper,
  StyledPostContentWrapper,
  StyledSocialPreview,
  StyledTagList,
  StyledTagTopic,
  StyledWrapper,
} from './index.styled'

export type Props = {
  author?: UserInfo
  post?: Post
  isMobile?: boolean
  title?: string
  id?: string
  community?: Community
  isPinned?: boolean
  isLiked?: boolean
  isReplied?: boolean
  postDate?: string
  slug?: string
  description?: string
  loveCount?: number
  commentCount?: number
  photos?: string[]
  previewUrl?: Post['previewUrl'] | null
  className?: string
  disableCardLink?: boolean
  disableShortenContent?: boolean
  topics?: Topic[]
  style?: CSSProperties
  onCommentClick?: () => void
  onCommentCountClick?: () => void
  onPostCommentSuccess?: (c: Comment) => void
  onDeletePost?: (postId: string) => void
  onEditPost?: (post: Post) => void
  displayComment?: Comment | null
  onDeleteComment?: (c: Comment) => void
  onViewMoreCommentClick?: (post: Post) => void
  showInputComment?: boolean
  InlineCommentsComponent?: ReactNode
  hasLinkWrapper?: boolean
  linkWrapperUrl?: string
  returnUrl?: string
  onDisplayCommentClick?: () => void
  handleRedirectSSO?: () => void
}

const CardPost = ({
  className,
  style,
  title,
  id,
  isPinned,
  topics,
  slug,
  author,
  community,
  postDate = '',
  photos,
  previewUrl,
  description,
  loveCount,
  isLiked,
  // isReplied,
  commentCount,
  onCommentClick,
  // onCommentCountClick,
  onPostCommentSuccess,
  onViewMoreCommentClick,
  disableCardLink,
  disableShortenContent,
  onDeletePost,
  onEditPost,
  post,
  displayComment: displayCommentProps,
  onDeleteComment,
  showInputComment: propsShowInputComment,
  InlineCommentsComponent,
  isMobile,
  hasLinkWrapper,
  // linkWrapperUrl,
  // returnUrl,
  onDisplayCommentClick,
  handleRedirectSSO,
  ...rest
}: Props) => {
  const { NEXT_PUBLIC_SSO: SSO_URL } = ENV
  const { formatMessage: f } = useIntl()
  const { locale = 'vi-VN', push, asPath } = useRouter()
  const { dispatch: togetherDataDispatch } = useContext(TogetherDataContext)
  const { state, pushNotifications } = useContext(AppContext)
  const { userInfo, isLoggedIn } = state || {}
  const inputCommentRef = useRef<ElementRef<typeof InputCommentBar>>(null)
  const inputCommentWrapperRef = useRef<HTMLDivElement>(null)
  const [showInputComment, setShowInputComment] = useState(
    propsShowInputComment,
  )
  const [isOpenSharePopup, setIsOpenSharePopup] = useState(false)
  const [displayComment, setDisplayComment] = useState<Comment | null>(
    displayCommentProps || null,
  )
  const [isEditingComment, setIsEditingComment] = useState(false)
  const { authActionWrapper } = useAuthRequiredAction(
    community?.slug
      ? {
          _joinCommunityIds: [community.id],
          _joinTopicIds: topics?.map((t) => t.id),
          _location: `/${community.slug}/${slug}`,
        }
      : undefined,
  )
  const { loading, onLikeUnlike } = useLikeUnlikePost({
    authActionWrapper,
  })
  // const { trigger } = useContext(MobileCommentTriggerContext)

  // useEffect(() => {
  //   if (!showInputComment) setShowInputComment(true)
  // }, [trigger])

  useEffect(() => {
    setShowInputComment(propsShowInputComment)
  }, [propsShowInputComment])

  useEffect(() => {
    showInputComment && setIsEditingComment(false)
  }, [showInputComment])

  const postRelateiveHref = `${_communityHref(
    `/${community?.slug || '_'}/${slug || ''}`,
  )}`
  // const postHref = `${getCurrentBaseUrl(locale)}${postRelateiveHref}`
  const currentDomain = domainLocales[locale || 'vi-VN']
  const currentProtocol =
    typeof window !== 'undefined' ? window.location.protocol : 'https:'
  const postHref = `${postRelateiveHref}`
  const shareUrl = `${currentProtocol}//${currentDomain}${postHref}`

  const name = post?.isAnonymous
    ? f({
        id: 'cardPost.anonymous',
        defaultMessage: 'Anonymous',
      })
    : author?.name || author?.username || ''
  const username = post?.isAnonymous
    ? f({
        id: 'cardPost.anonymous',
        defaultMessage: 'Anonymous',
      })
    : author?.username || author?.name || ''
  const avatar = post?.isAnonymous ? DEFAULT_AVATAR_IMG : author?.avatar
  const authorRole = post?.isAnonymous ? undefined : author?.role

  useEffect(() => {
    const hash = window.location.hash
    if (hash === '#comment') {
      setShowInputComment(true)

      setTimeout(() => {
        if (inputCommentWrapperRef?.current) {
          const top =
            inputCommentWrapperRef.current.getBoundingClientRect().top +
            window.scrollY -
            80
          window.scrollTo({ top: top, behavior: 'smooth' })
        }
      }, 1000)
      setTimeout(() => {
        inputCommentRef?.current?.focusEditor()
      }, 1500)
    }
  }, [asPath])

  useEffect(() => {
    setDisplayComment(displayCommentProps || null)
  }, [displayCommentProps])

  return (
    // <AuthConfigContextProvider
    //   defaultParams={
    //     community?.slug
    //       ? {
    //           _joinCommunityIds: [community.id],
    //           _joinTopicIds: topics?.map((t) => t.id),
    //           _location: `/${community.slug}/${slug}`,
    //         }
    //       : undefined
    //   }
    // >
    <>
      {isOpenSharePopup && (
        <SharePopup
          url={shareUrl}
          trackingCategory={TRACKING_CATEGORY}
          avatar={DEFAULT_IMG}
          text={title}
          onClose={() => setIsOpenSharePopup(false)}
        />
      )}
      <StyledWrapper className={className} style={style} {...rest}>
        <StyledCardPost>
          <StyledInnerPadding>
            <StyledCardAuthorWrapper
              authorProps={{
                avatar: avatar,
                name: name,
                username: username,
                url:
                  author?.email === userInfo?.email
                    ? SSO_URL + DISCOVER_PATHS.GET_PROFILE
                    : '',
                email: author?.email,
                time: post?.scheduleDate || post?.createdDate || postDate,
                community: (community as unknown) as TogetherTypes.Community,
                // miniIcon="./svg/icon-tag.svg"
                className: `post-author with-popover`,
                role: authorRole,
                title: author?.title,
                userId: author?.id,
                expertId: post?.author?.partnerId,
                dataEventCategory: TRACKING_CATEGORY,
                dataEventAction: 'Click Post',
                dataEventLabel: postHref,
                communityTrackingAttr: {
                  'data-event-category': TRACKING_CATEGORY,
                  'data-event-action': 'Community Title Click',
                  'data-event-label':
                    // getCurrentBaseUrl(locale) +
                    _communityHref(`/${community?.slug || ''}`),
                },
              }}
            >
              {isPinned && (
                <StyledPin>
                  <img
                    loading="lazy"
                    alt=""
                    className="label-sort-pin-icon"
                    src={_communityHref('/svg/icon-pin-yellow.svg')}
                  />
                </StyledPin>
              )}
              <CardPostPopoverMore
                url={postHref}
                authorEmail={String(author?.email)}
                onDeletePost={onDeletePost}
                onEditPost={(p) => p?.id && onEditPost?.(p)}
                shareUrl={shareUrl}
                post={post}
                isMobile={isMobile}
                className="popover"
                redirectToSSOWithReturnUrl={redirectToSSOWithReturnUrl}
              />
            </StyledCardAuthorWrapper>
            <StyledPostContentWrapper>
              <StyledPostContentLinkWrapper
                className={`post-content-link-wrapper`}
              >
                {title && (
                  <Heading
                    className="post-content-title"
                    as="h1"
                    tag="h5"
                    fontWeight="bold"
                  >
                    {innertext(title)}
                  </Heading>
                )}
                <StyledPostContent
                  trackingCategory={TRACKING_CATEGORY}
                  url={postHref}
                  className="post-content-wrapper"
                  description={description}
                  disableShortenContent={disableShortenContent}
                  mentions={post?.mentions}
                />
              </StyledPostContentLinkWrapper>
              {hasLinkWrapper && !disableCardLink && (
                <StyledLinkOverlay
                  href={postHref}
                  {...{
                    'data-even-category': TRACKING_CATEGORY,
                    'data-even-action': 'Click Post',
                    'data-event-label': postHref,
                  }}
                  onClick={() => {
                    window.location.href = postHref
                  }}
                />
              )}
            </StyledPostContentWrapper>
          </StyledInnerPadding>
          <StyledTagList key="tag-topic">
            {topics && topics.length > 0 ? (
              <StyledTagTopic
                topics={topics as TogetherTypes.Topic[]}
                className="post-topics"
              />
            ) : null}
          </StyledTagList>
          <StyledSocialPreview>
            {post?.introUrl ? (
              <div key="social-preview">
                <div
                  className={`social-preview-wrapper ${
                    !!photos?.length ? 'has-image' : ''
                  }`}
                >
                  <SocialLinkPreview
                    url={post.introUrl}
                    image={post.bannerImage}
                  />
                </div>
              </div>
            ) : null}
            {!!photos?.length ? (
              <div key="image-preview">
                <div
                  className="image-preview-wrapper"
                  data-sensitive={Boolean(post?.isSensitive)}
                >
                  <PostImagePreview
                    images={photos}
                    authorName={author?.name}
                    isSensitive={Boolean(post?.isSensitive)}
                  />
                </div>
              </div>
            ) : null}
            {!post?.introUrl && !photos?.length && previewUrl?.url ? (
              <div key="link-preview">
                <div className="link-preview-wrapper">
                  <CardLink
                    title={previewUrl.title}
                    url={previewUrl.url}
                    image={previewUrl.image}
                  />
                </div>
              </div>
            ) : null}
          </StyledSocialPreview>
          <StyledInnerPadding>
            <StyledCardReactions
              // postUrl={postRelateiveHref}
              trackingCategory={TRACKING_CATEGORY}
              trackingLabel={postHref}
              type={CardReactionType.post}
              loveCount={loveCount}
              replyCount={commentCount}
              className={`post-reaction ${
                photos?.length || previewUrl?.url ? 'hide-top-border' : ''
              }`}
              liked={loading ? !isLiked : isLiked}
              isVertical={true}
              isSaved={post?.bookmarked || false}
              // isReplied={isReplied}
              onCommentClick={() => {
                isLoggedIn
                  ? onCommentClick
                    ? onCommentClick()
                    : setShowInputComment((v) => !v)
                  : redirectToSSOWithReturnUrl()
              }}
              onShareClick={() => {
                setIsOpenSharePopup(true)
              }}
              onLikeClick={() => {
                if (isLoggedIn) {
                  id && onLikeUnlike({ id, isLike: !isLiked })
                } else {
                  redirectToSSOWithReturnUrl?.()
                }
              }}
              onSaveClick={async () => {
                if (isLoggedIn) {
                  if (!userInfo?.id || !post?.id) return
                  if (post?.bookmarked) {
                    const res = await callApiWithAuth(
                      getTogetherPath(PATHS.BOOKMARK.DELETE, {
                        _locale: locale,
                        postId: post?.id,
                      }),
                      'DELETE',
                      {
                        data: {
                          post_id: String(post.id),
                        },
                      },
                    )
                    if (res?.status === 1) {
                      pushNotifications({
                        title: '',
                        message: f({
                          id: 'post.remove.success',
                          defaultMessage: 'Removed saved post',
                        }),
                        type: 'success',
                        displayIcon: '/askDoctor/icon-bookmark.svg',
                      })
                      togetherDataDispatch({
                        type: PostActions.update,
                        payload: {
                          id: post.id,
                          data: {
                            bookmarked: false,
                          },
                        },
                      })
                    } else {
                      pushNotifications({
                        title: '',
                        message: f({
                          id: 'post.remove.failed',
                          defaultMessage: 'Remove failed',
                        }),
                        type: 'danger',
                        displayIcon: '/askDoctor/icon-bookmark.svg',
                      })
                    }
                  } else {
                    const res = await callApiWithAuth(
                      getTogetherPath(PATHS.BOOKMARK.CREATE, {
                        _locale: locale,
                      }),
                      'POST',
                      {
                        data: {
                          post_id: post.id,
                        },
                      },
                    )
                    if (res?.status === 1) {
                      pushNotifications({
                        title: '',
                        message: f({
                          id: 'post.save.success',
                          defaultMessage: 'Post saved',
                        }),
                        type: 'success',
                        displayIcon: '/askDoctor/icon-bookmark-fill.svg',
                      })
                      togetherDataDispatch({
                        type: PostActions.update,
                        payload: {
                          id: post.id,
                          data: {
                            bookmarked: true,
                          },
                        },
                      })
                    } else {
                      pushNotifications({
                        title: '',
                        message: f({
                          id: 'post.save.failed',
                          defaultMessage: 'Post saved failed',
                        }),
                        type: 'danger',
                        displayIcon: '/askDoctor/icon-bookmark.svg',
                      })
                    }
                  }
                } else {
                  redirectToSSOWithReturnUrl?.()
                }
              }}
            />
            <div ref={inputCommentWrapperRef}>
              {showInputComment && (
                <StyledInputCommentBar
                  ref={inputCommentRef}
                  isMobile={isMobile}
                  isMainCmt={!propsShowInputComment}
                  onClose={() => setShowInputComment(false)}
                  onPostSuccess={(comment) => {
                    onPostCommentSuccess?.(comment)
                    setShowInputComment(false)
                  }}
                  post={post}
                  btnProps={{
                    dataEventCategory: TRACKING_CATEGORY,
                    dataEventAction: 'Submit Comment',
                    dataEventLabel: postHref,
                  }}
                  expandPopupProps={{
                    heading: f({
                      id: 'cardPost.inputComment.heading',
                      defaultMessage: 'Comment',
                    }),
                    note: f(
                      {
                        id: 'cardPost.inputComment.note',
                        defaultMessage: 'Comment on {title}',
                      },
                      {
                        title: `<strong>#${post?.title}</strong>`,
                      },
                    ),
                  }}
                />
              )}
            </div>
          </StyledInnerPadding>
        </StyledCardPost>
        {displayComment && (
          <StyledCardReplyWrapper
            className="most-engaging-wrapper"
            key={displayComment.id}
            onClick={() => {
              if (!isEditingComment) {
                onDisplayCommentClick
                  ? onDisplayCommentClick()
                  : push(`/${community?.slug || '_'}/${slug}`)
              }
            }}
          >
            <CardReply
              isMobile={isMobile}
              post={post}
              comment={displayComment}
              handleRedirectSSO={handleRedirectSSO}
              onDeleteComment={() => {
                onDeleteComment && onDeleteComment(displayComment)
              }}
              onOpenEditInput={() => {
                setIsEditingComment(true)
              }}
              onEditCommentSuccess={(comment) => {
                setDisplayComment(comment)
                setIsEditingComment(false)
              }}
              onViewMoreCommentClick={onViewMoreCommentClick}
              className="most-engaging-comment"
              type="most-engaging"
              onClick={() => push(`/${community?.slug || '_'}/${slug}`)}
            />
          </StyledCardReplyWrapper>
        )}
        {InlineCommentsComponent}
      </StyledWrapper>
    </>
    // </AuthConfigContextProvider>
  )
}

export const CardPostWithCommentData = ({
  id,
  post,
  isMobile,
  onDeletePost,
  onEditPost,
  ...rest
}: Props) => {
  const { locale = 'vi-VN' } = useRouter()

  const [comments, setComments] = useState<Comment[]>([])
  const [postCommentCount, setPostCommentCount] = useState(
    post?.commentCount || 0,
  )
  // const [commentsPagination, setCommentsPagination] = useState<{
  //   current: number
  //   last: number
  //   total_items: number
  // }>({
  //   current: 1,
  //   last: 1,
  //   total_items: 0,
  // })
  useEffect(() => {
    ;(async () => {
      if (id && postCommentCount) {
        const commentsRes = await callApiWithAuth(
          getTogetherPath(TOGETHER_PATHS.COMMENT.LIST, {
            postId: id,
            sort: 'DESC',
            page: 1,
            websiteUrl: 'https://marrybaby.vn',
          }),
          'GET',
        )
        setComments(commentsRes?.data?.comments?.map(formatComment) || [])
        // setCommentsPagination(
        //   commentsRes?.data?.pagination || {
        //     current: 1,
        //     last: 1,
        //     total_items: 0,
        //   },
        // )
      }
    })()
  }, [id, locale, postCommentCount])
  const onPostCommentSuccess = (comment: Comment) => {
    setPostCommentCount(postCommentCount + 1)
    setComments((old) => {
      old.unshift(comment)
      return old.slice()
    })
    // setCommentsPagination((_old) => ({
    //   ..._old,
    //   total_items: _old.total_items + 1,
    // }))
  }

  const onDeleteComment = (comment: Comment) => {
    setPostCommentCount(postCommentCount - 1)
    setComments((_comments) => {
      const foundIndex = _comments.findIndex((c) => c.id === comment.id)
      _comments.splice(foundIndex, 1)
      return _comments.slice()
    })
    // setCommentsPagination((_old) => ({
    //   ..._old,
    //   total_items: _old.total_items - 1,
    // }))
  }

  return (
    <CardPost
      id={id}
      commentCount={postCommentCount}
      isReplied={post?.commented}
      onPostCommentSuccess={onPostCommentSuccess}
      displayComment={comments?.[0]}
      onDeleteComment={onDeleteComment}
      onDeletePost={onDeletePost}
      onEditPost={onEditPost}
      post={post}
      isMobile={isMobile}
      {...rest}
    />
  )
}

export { CardPost }
